import { useGraphQLQuery } from '@betterment/graphql';
import { Button, FormikField as Field, Input, Label, Text, ToggleGroup, VStack } from '@betterment/samba-react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';

import {
  NameAndImagePurposeCreationOptionsDocument,
  NameAndImagePurposeCreationOptionsQuery,
} from './graphql/query.purposeCreationOption.generated';
import { ImageSelector } from './ImageSelector';
import { nameAndImage } from './sharedSchemas';

import { PurposeCreationIntent } from '../../graphql/schema';

type FormValues = Yup.InferType<typeof nameAndImage>;

type Props = {
  handleSubmit: (values: FormValues) => void;
  intent: PurposeCreationIntent;
};

interface AdviceTypeToggleGroupProps {
  purposeCreationOptions: NameAndImagePurposeCreationOptionsQuery['purposeCreationOptions'];
}

const AdviceTypeToggleGroup: React.FC<AdviceTypeToggleGroupProps> = ({ purposeCreationOptions }) => {
  const { setFieldValue } = useFormikContext<FormValues>();

  const handleToggleChange = (value: string) => {
    const option = purposeCreationOptions.find(opt => opt.id === value) || purposeCreationOptions[0];
    void setFieldValue('name', option.defaultName);
    void setFieldValue('imageType', option.purposeAsset.legacyImageKey);
    void setFieldValue('purpose_sub_type', option.legacyAdviceTypeId);
  };

  return (
    <VStack css={{ gap: 'space.200' }}>
      <Label>What are you saving for?</Label>
      <ToggleGroup
        spacing="default"
        type="single"
        css={{ backgroundColor: 'background.surface', padding: 'space.0', flexWrap: 'wrap', gap: 'space.100' }}
        onValueChange={handleToggleChange}
      >
        {purposeCreationOptions.map(option => (
          <ToggleGroup.Item
            value={option.id}
            key={option.id}
            css={{
              flex: '0 1 auto',
              backgroundColor: 'background.surface',
              borderColor: 'border.weak',
              borderStyle: 'solid',
              borderWidth: 'thin',
              borderRadius: 'round',
            }}
          >
            <Text css={{ px: 'space.100' }}>{option.defaultName}</Text>
          </ToggleGroup.Item>
        ))}
      </ToggleGroup>
    </VStack>
  );
};

export const NameAndImageForm: React.FC<Props> = ({ handleSubmit, intent }) => {
  const { data } = useGraphQLQuery(NameAndImagePurposeCreationOptionsDocument, {
    variables: { intent },
  });

  if (!data) return null;

  const { defaultName, purposeAsset } = data.purposeCreationOptions[0];

  const initialValues = {
    name: intent === PurposeCreationIntent.MajorPurchase ? '' : defaultName,
    purpose_sub_type:
      intent === PurposeCreationIntent.MajorPurchase ? '' : data.purposeCreationOptions[0].legacyAdviceTypeId,
    imageType: data.purposeAssets.find(asset => asset.icon.id === purposeAsset.icon.id)?.legacyImageKey,
    customImageData: '',
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={nameAndImage}
      validateOnChange={false}
      validateOnBlur={false}
      enableReinitialize={true}
    >
      <Form>
        <VStack css={{ gap: 'space.400' }}>
          {intent === PurposeCreationIntent.MajorPurchase && (
            <AdviceTypeToggleGroup purposeCreationOptions={data.purposeCreationOptions} />
          )}
          <VStack css={{ gap: 'space.400' }}>
            <Field name="name">
              <Field.Label>Goal name</Field.Label>
              <Field.Control>
                <Input />
              </Field.Control>
              <Field.Description>You can change this name anytime.</Field.Description>
              <Field.ErrorMessage />
            </Field>

            <ImageSelector themedAsset={purposeAsset.icon} alt={defaultName} />
            <Button type="submit" variant="solidPrimary" css={{ width: 'fit-content' }}>
              Continue
            </Button>
          </VStack>
        </VStack>
      </Form>
    </Formik>
  );
};
